import { useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import { Link, useSearchParams } from "react-router-dom";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import GameIcon from "components/admin/GameIcon";
import PlayerSearch from "components/admin/PlayerSearch";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";

const REASONS = [
  "Giá bất thường, nghi rửa tiền",
  "Đồ có lỗi / do lỗi game mà có",
  "Người chơi nhờ gỡ hộ",
  "Vi phạm quy định giao dịch",
];

function RemoveDialog({ row, onClose, onDone }) {
  const [run, busy] = useRunner();
  const [reason, setReason] = useState("");
  const submit = async () => {
    const { ok } = await run(
      () =>
        api.post("/api/admin/market/remove", {
          kioskType: row.kioskType,
          sellId: row.sellId,
          reason,
        }),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={`Gỡ ${row.name} khỏi ki ốt`}
      color="error"
      submitText="Gỡ và trả đồ"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
    >
      <MDTypography variant="button" color="text">
        Người bán: {row.sellerName} ({row.sellerOnline ? "đang online" : "offline"}) · Giá{" "}
        {formatNumber(row.currentPrice ?? row.price)} ngọc
      </MDTypography>
      <MDInput
        label="Lý do (người bán đọc trong thư)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <MDBox display="flex" gap={1} flexWrap="wrap" mt={-1}>
        {REASONS.map((r) => (
          <Chip
            key={r}
            size="small"
            variant={reason === r ? "filled" : "outlined"}
            color={reason === r ? "info" : "default"}
            label={r}
            onClick={() => setReason(r)}
          />
        ))}
      </MDBox>
      <Alert severity="info">
        <b>Người bán sẽ nhận thư:</b> &quot;{row.name} của bạn đã bị quản trị viên gỡ khỏi ki ốt và
        trả về {row.sellerOnline ? "hành trang" : "hành trang ở lần đăng nhập tới"}.
        {reason.trim() ? ` Lý do: ${reason.trim()}` : ""}&quot;
        {row.retail && (
          <>
            <br />
            Món bán lẻ: phần đã bán được ({formatNumber(row.sumVal)} ngọc) cũng trả về người bán.
          </>
        )}
      </Alert>
      {!reason.trim() && (
        <MDTypography variant="caption" color="warning">
          Chưa có lý do — người bán sẽ không biết vì sao bị gỡ.
        </MDTypography>
      )}
    </ActionDialog>
  );
}

RemoveDialog.propTypes = {
  row: PropTypes.shape({
    kioskType: PropTypes.number,
    sellId: PropTypes.number,
    name: PropTypes.string,
    sellerName: PropTypes.string,
    price: PropTypes.number,
    currentPrice: PropTypes.number,
    sellerOnline: PropTypes.bool,
    retail: PropTypes.bool,
    sumVal: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function itemDetail(row) {
  if (row.isPet) {
    const p = row.pet;
    return p ? `Cấp ${p.lvl} · ${p.star} sao · ${p.str}/${p.agi}/${p._int}` : "-";
  }
  const i = row.item;
  if (!i) return "-";
  const parts = [];
  if (i.lvl > 0) parts.push(`+${i.lvl}`);
  if (i.stackable) parts.push(`x${formatNumber(i.count)}`);
  if (i.numFusion > 0) parts.push(`dung hợp ${i.numFusion}`);
  return parts.join(" · ") || "-";
}

function Market() {
  const canEdit = useCan(PERMISSIONS.MARKET_EDIT);
  const canPlayer = useCan(PERMISSIONS.PLAYER_VIEW);
  const [params, setParams] = useSearchParams();
  // ?seller=<userId>&sellerName=... lọc đồ của một người bán (link từ trang người chơi).
  const sellerId = Number(params.get("seller")) || 0;
  const sellerName = params.get("sellerName") || (sellerId ? `tài khoản ${sellerId}` : "");
  const setSeller = (person) => {
    setPage(1);
    setParams(person ? { seller: person.userId, sellerName: person.name || "" } : {}, {
      replace: true,
    });
  };
  const [kiosk, setKiosk] = useState(-1);
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [removing, setRemoving] = useState(null);
  const list = useFetch(
    () =>
      api.get("/api/admin/market", {
        kiosk,
        q: applied,
        userId: sellerId || undefined,
        page,
        size,
      }),
    [kiosk, applied, sellerId, page, size]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="market" />
        <PageCard
          title="Ki ốt"
          subtitle="Đồ người chơi đang treo bán ở chợ"
          noPadding
          actions={
            <MDButton size="small" color="white" onClick={() => list.reload()}>
              <Icon>refresh</Icon>&nbsp;Làm mới
            </MDButton>
          }
        >
          <MDBox
            px={2}
            pb={1}
            component="form"
            display="flex"
            gap={2}
            flexWrap="wrap"
            alignItems="center"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setApplied(q);
            }}
          >
            <MDInput
              select
              label="Ki ốt"
              value={kiosk}
              onChange={(e) => {
                setKiosk(e.target.value);
                setPage(1);
              }}
              SelectProps={{ sx: { height: 44 } }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value={-1}>Tất cả</MenuItem>
              {(list.data?.kiosks || []).map((k) => (
                <MenuItem key={k.type} value={k.type}>
                  {k.name} ({k.count})
                </MenuItem>
              ))}
            </MDInput>
            <MDInput
              label="Tên món hoặc người được chỉ định mua"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <MDButton type="submit" variant="gradient" color="info">
              Tìm
            </MDButton>
          </MDBox>
          <MDBox px={2} pb={2}>
            {sellerId ? (
              <Chip
                color="info"
                label={`Chỉ xem đồ của ${sellerName}`}
                onDelete={() => setSeller(null)}
              />
            ) : (
              <>
                <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                  Xem một người đang treo bán gì:
                </MDTypography>
                <PlayerSearch onPick={setSeller} buttonText="Lọc" />
              </>
            )}
          </MDBox>
          {list.error && <Alert severity="error">{list.error}</Alert>}
          <AdminTable
            loading={list.loading}
            rows={list.data?.items || []}
            rowKey={(r) => `${r.kioskType}-${r.sellId}`}
            page={page}
            size={size}
            total={list.data?.total ?? 0}
            onPageChange={setPage}
            onSizeChange={(s) => {
              setSize(s);
              setPage(1);
            }}
            emptyText={
              sellerId ? `${sellerName} không treo bán món nào` : "Không có món nào đang treo"
            }
            columns={[
              {
                key: "name",
                label: "Món",
                render: (r) => (
                  <MDBox display="flex" gap={1} alignItems="center">
                    <GameIcon
                      path={r.isPet ? r.pet?.icon : r.item?.icon}
                      size={28}
                      fallback={r.isPet ? "pets" : "inventory_2"}
                    />
                    <MDBox>
                      {r.name}
                      <MDTypography variant="caption" color="text" display="block">
                        {r.kioskName} · {itemDetail(r)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                ),
              },
              {
                key: "price",
                label: "Giá (ngọc)",
                align: "right",
                render: (r) => (
                  <MDBox>
                    {formatNumber(r.currentPrice)}
                    {r.retail && (
                      <MDTypography variant="caption" color="text" display="block">
                        Bán lẻ, đã thu {formatNumber(r.sumVal)}
                      </MDTypography>
                    )}
                  </MDBox>
                ),
              },
              {
                key: "seller",
                label: "Người bán",
                render: (r) => (
                  <MDBox display="flex" gap={1} alignItems="center">
                    {canPlayer ? (
                      <MDTypography
                        component={Link}
                        to={`/players/${r.sellerId}`}
                        variant="button"
                        color="info"
                      >
                        {r.sellerName}
                      </MDTypography>
                    ) : (
                      r.sellerName
                    )}
                    {r.sellerOnline && <Chip size="small" color="success" label="Online" />}
                  </MDBox>
                ),
              },
              {
                key: "assignedName",
                label: "Chỉ bán cho",
                render: (r) => r.assignedName || "-",
              },
              {
                key: "expireTime",
                label: "Hết hạn",
                render: (r) => formatDate(r.expireTime),
              },
              canEdit && {
                key: "actions",
                label: "",
                align: "right",
                render: (r) => (
                  <MDButton
                    size="small"
                    variant="text"
                    color="error"
                    onClick={() => setRemoving(r)}
                  >
                    <Icon>remove_shopping_cart</Icon>&nbsp;Gỡ
                  </MDButton>
                ),
              },
            ].filter(Boolean)}
          />
        </PageCard>
      </MDBox>
      <Footer />
      {removing && (
        <RemoveDialog
          row={removing}
          onClose={() => setRemoving(null)}
          onDone={() => list.reload(true)}
        />
      )}
    </DashboardLayout>
  );
}

export default Market;
